<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="12">
            <!-- Type Banner -->
            <import-page-banner
                :title="data.config?.displayName || data.type"
                :sample-csv-path="getSampleCsvLink()"
            />

            <v-card>
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="6">
                            <h2>{{ data.labelText?.importPageTitle || 'Import Master Data' }}</h2>
                        </v-col>
                        <v-col cols="12" sm="12" md="6"></v-col>
                    </v-row>
                </v-card-title>
                <v-card-text>
                    <p>
                        <b>Only accept csv file.</b>
                    </p>
                    <p>1. Choose your file</p>
                    <p>2. SELECT THE CORRECT MAPPING OF Field and Column</p>
                    <p>3. IMPORT</p>
                    <p>
                        <b>Important:</b> Maximum {{ maxRowLimit }} records per import. For larger imports, please split
                        your CSV file into multiple batches.
                    </p>
                    <p><b>Note:</b> Required columns are: <b>code</b> and <b>name</b></p>
                    <!-- New CSV Importer Component -->
                    <csv-importer
                        :field-definitions="data.fields"
                        :preview-headers="data.previewHeaders"
                        @data-loaded="onCsvDataLoaded"
                        @data-cleared="onCsvDataCleared"
                    />
                </v-card-text>
            </v-card>
            <br />
            <v-card v-if="data.mappedCsv && data.mappedCsv.length > 0">
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="12">
                            <v-btn
                                class="mr-2"
                                color="primary"
                                :loading="loading"
                                :disabled="loading || invalidFile"
                                @click="onImportClick"
                            >
                                <v-icon>mdi-cloud-upload</v-icon>
                                Import
                            </v-btn>
                            <v-btn class="mr-2" @click="onBackClick"> Back to list </v-btn>
                            <span v-if="loading && totalRows > 0" class="ml-2">
                                Processing {{ currentProgress }}/{{ totalRows }} ({{
                                    Math.round((currentProgress / totalRows) * 100)
                                }}%)
                            </span>
                            <span v-if="importDone">{{ importDoneMsg }}</span>
                        </v-col>
                    </v-row>
                </v-card-title>
                <v-card-text>
                    <v-table>
                        <thead>
                            <tr>
                                <th class="text-left" v-for="header in data.headers" :key="header.value">
                                    {{ header.text }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, idx) in transformMappedCsv(data.mappedCsv)"
                                :key="`row${idx}`"
                                :class="invalidRowClass(item)"
                            >
                                <td v-for="header in data.headers" :key="header.value" style="white-space: pre-line">
                                    {{ item[header.value] }}
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive } from 'vue';
import { fetchWrapper } from '../../utils/FetchWrapper';
import * as _ from 'lodash';
import { PathDefinition } from '../../utils/PathDefinition';
import { ImportTableLabelTextHelper } from '../../utils/labelTextHelper/ImportTableLabelTextHelper';
import { SharedMsgHelper } from '../../utils/SharedMsgHelper';
import { MasterDataHeaderHelper, MASTER_DATA_CONFIG } from '../../utils/headerHelper/MasterDataHeaderHelper';
import { MasterDataLabelTextHelper } from '../../utils/labelTextHelper/MasterDataLabelTextHelper';
import { ImportHeaderHelper } from '../../utils/ImportHeaderHelper';
import CsvImporter from '../../components/CsvImporter.vue';
import ImportPageBanner from '../../components/ImportPageBanner.vue';

export default defineComponent({
    name: 'ImportMasterData',

    components: {
        CsvImporter,
        ImportPageBanner
    },

    setup() {
        const data = reactive({
            type: '',
            config: null as any,
            labelText: null as any,
            mappedCsv: null as any,
            fields: {} as any,
            previewHeaders: [] as any[],
            headers: [] as any[]
        });
        return {
            data
        };
    },

    data() {
        return {
            loading: false,
            invalidFile: false,
            importDone: false,
            importDoneMsg: SharedMsgHelper.getImportDoneMsg,
            maxRowLimit: 200,
            currentProgress: 0,
            totalRows: 0
        };
    },

    mounted() {
        // Get type from route params
        this.data.type = this.$route.params.type as string;

        // Validate type
        if (!MASTER_DATA_CONFIG[this.data.type]) {
            alert(`Invalid master data type: ${this.data.type}`);
            this.$router.push({ path: PathDefinition.masterData });
            return;
        }

        // Initialize config and labels
        this.data.config = MASTER_DATA_CONFIG[this.data.type];
        this.data.labelText = MasterDataLabelTextHelper.getLabelTextForType(this.data.type);
        this.data.fields = MasterDataHeaderHelper.getImportFieldsForType(this.data.type);
        this.data.previewHeaders = MasterDataHeaderHelper.getImportHeadersForType(this.data.type);
        this.data.headers = [
            ...ImportHeaderHelper.getHeaders(),
            ...MasterDataHeaderHelper.getImportHeadersForType(this.data.type)
        ];
    },

    methods: {
        onCsvDataLoaded(mappedData) {
            // Receive data from CSV importer component
            this.data.mappedCsv = mappedData;
            // Validate mapping immediately after data is loaded
            this.validateColumnMapping();
        },
        validateColumnMapping() {
            if (!this.data.mappedCsv || this.data.mappedCsv.length === 0) {
                return;
            }

            const codeField = this.data.config.codeField;
            const nameField = this.data.config.nameField;
            const typeName = this.data.config.displayName;

            // Get sample data to check column names from CSV
            const firstRow = this.data.mappedCsv[0];
            const mappedFields = Object.keys(firstRow).filter(
                (key) => key !== 'status' && key !== 'result' && key !== 'error'
            );

            // Expected patterns for each type
            const typePatterns: Record<string, { code: RegExp; name: RegExp }> = {
                currency: { code: /^currency.*code$/i, name: /^currency.*name$/i },
                marketplace: { code: /^marketplace.*code$/i, name: /^marketplace.*name$/i },
                productCategory: { code: /^category.*code$/i, name: /^category.*name$/i }
            };

            const pattern = typePatterns[this.data.type];
            if (!pattern) return;

            // Check if the mapped fields match the expected type pattern
            const codeMatches = pattern.code.test(codeField);
            const nameMatches = pattern.name.test(nameField);

            // Detect suspicious mapping
            let suspiciousMapping = false;
            let detectedType = '';

            // Check if columns match a different type
            Object.entries(typePatterns).forEach(([type, typePattern]) => {
                if (type !== this.data.type) {
                    if (typePattern.code.test(codeField) || typePattern.name.test(nameField)) {
                        suspiciousMapping = true;
                        detectedType = type;
                    }
                }
            });

            if (suspiciousMapping) {
                const userConfirmed = confirm(
                    `⚠️ WARNING: Potential data type mismatch detected!\n\n` +
                        `You are importing ${typeName} data, but the CSV columns appear to be for ${detectedType}.\n\n` +
                        `Expected fields:\n` +
                        `  • ${codeField}\n` +
                        `  • ${nameField}\n\n` +
                        `This may result in incorrect data being imported.\n\n` +
                        `Do you want to continue anyway?`
                );

                if (!userConfirmed) {
                    // Clear the data
                    this.data.mappedCsv = null;
                    this.invalidFile = true;
                    alert('Import cancelled. Please select the correct master data type or use the correct CSV file.');
                }
            }
        },
        onCsvDataCleared() {
            // Clear data when file is removed
            this.data.mappedCsv = null;
            this.importDone = false;
            this.invalidFile = false;
        },
        transformMappedCsv(mappedCsv: any) {
            if (!mappedCsv) return [];

            const codeField = this.data.config.codeField;
            const nameField = this.data.config.nameField;
            const requiredFields = [codeField, nameField];

            return _.map(mappedCsv, (item) => {
                let result = _.mapValues(_.pick(item, [codeField, nameField, 'status', 'result', 'error']), (value) =>
                    _.trim(value)
                );

                // simple validation rules, avoid over-engineering, skip the import validation config approach
                requiredFields.forEach((field) => {
                    if (!result[field]) {
                        result[field] = SharedMsgHelper.getMissingRequiredErrMsg;
                    }
                });

                return result;
            });
        },

        async onImportClick() {
            this.data.mappedCsv = this.transformMappedCsv(this.data.mappedCsv);
            if (this.data.mappedCsv && this.data.mappedCsv.length > 0) {
                // Check row limit
                if (this.data.mappedCsv.length > this.maxRowLimit) {
                    alert(
                        `Too many rows. Maximum ${this.maxRowLimit} records allowed per import.\n\nYour file has ${this.data.mappedCsv.length} rows.\n\nPlease split your CSV file into smaller batches.`
                    );
                    return;
                }
                await this.importSubmit();
            } else {
                console.log('empty csv');
            }
        },

        async importSubmit() {
            this.loading = true;
            this.currentProgress = 0;
            this.totalRows = this.data.mappedCsv.length;

            const isValid = this.validation(this.data.mappedCsv);
            if (isValid) {
                for (let i = 0; i < this.data.mappedCsv.length; i++) {
                    await this.create(i);
                    this.currentProgress = i + 1;
                }
                this.importDone = true;
                // prevent import again
                this.invalidFile = true;
            } else {
                console.log('invalid csv');
            }
            this.loading = false;
            this.currentProgress = 0;
            this.totalRows = 0;
        },

        validation(list: any[]) {
            return _.every(list, (v) => {
                return !this.isInvalidRow(v);
            });
        },

        invalidRowClass(item: any) {
            if (this.isInvalidRow(item)) {
                return 'row-validation-error';
            }
            return '';
        },

        isInvalidRow(item: any) {
            return (
                this.isInvalidRowContent(item) || item.status === ImportTableLabelTextHelper.getImportResultValue.error
            );
        },

        isInvalidRowContent(item: any) {
            const codeField = this.data.config.codeField;
            const nameField = this.data.config.nameField;

            return (
                item[codeField] === SharedMsgHelper.getMissingRequiredErrMsg ||
                item[nameField] === SharedMsgHelper.getMissingRequiredErrMsg
            );
        },

        async create(idx: number) {
            const csvData = this.data.mappedCsv[idx];
            const codeField = this.data.config.codeField;
            const nameField = this.data.config.nameField;

            const requestUrl = `/api/staff/${this.data.config.apiPath}/create`;
            const requestData = {
                [codeField]: csvData[codeField],
                [nameField]: csvData[nameField]
            };

            try {
                const response = await fetchWrapper.post(requestUrl, requestData);

                if (response.success) {
                    csvData.result = 'Success';
                    csvData.status = ImportTableLabelTextHelper.getImportResultValue.success;
                    csvData.error = '';
                } else {
                    if (this.isAlreadyExistsError(response)) {
                        csvData.result = 'Already exists';
                        csvData.status = ImportTableLabelTextHelper.getImportResultValue.error;
                        csvData.error = 'Record with this code already exists';
                    } else {
                        csvData.result = 'Failed';
                        csvData.status = ImportTableLabelTextHelper.getImportResultValue.error;
                        csvData.error = this.extractErrorMessage(response);
                    }
                }
            } catch (error) {
                csvData.result = 'Failed';
                csvData.status = ImportTableLabelTextHelper.getImportResultValue.error;
                csvData.error = 'Unexpected error occurred';
            }

            this.data.mappedCsv.splice(idx, 1, csvData);
        },

        isAlreadyExistsError(response: any) {
            const errorMsg = response.errors || '';
            return (
                (errorMsg.includes('CONFLICT') && errorMsg.includes('already exists')) ||
                errorMsg.includes('already exists')
            );
        },

        extractErrorMessage(response: any) {
            const errors = response.errors || '';
            // Handle different error formats
            if (typeof errors === 'object') {
                return JSON.stringify(errors);
            }
            // Extract just the message part if it's formatted as "CODE: Message"
            if (typeof errors === 'string' && errors.includes(':')) {
                return errors.split(':').slice(1).join(':').trim();
            }
            return String(errors);
        },

        async onBackClick() {
            await this.$router.push({ path: PathDefinition.masterData });
        },

        getSampleCsvLink(): string {
            const sampleFiles: Record<string, string> = {
                currency: '/importCurrencySample.csv',
                marketplace: '/importMarketplaceSample.csv',
                productCategory: '/importProductCategorySample.csv'
            };
            return sampleFiles[this.data.type] || '';
        }
    }
});
</script>

<style>
select {
    border: 1px solid !important;
}

.row-validation-error {
    background: red;
}
</style>
